// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authApi from '../api/authApi';
import apiClient from '../api/apiClient';
import { setToken, removeToken, isAuthenticated } from '../utils/tokenStorage';

// Create context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [humanVerificationEnabled, setHumanVerificationEnabled] = useState(false);
  const navigate = useNavigate();

  // Check if user is already authenticated on mount and fetch configuration
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        setUser({ authenticated: true });
      }
      
      // Check if human verification is enabled from backend config endpoint
      try {
        const response = await apiClient.get('/config');
        const config = response.data;
        setHumanVerificationEnabled(config.enableHumanVerification);
        
        // Log the config
        logDebug('AuthContext loaded config', { 
          config, 
          humanVerificationEnabled: config.enableHumanVerification 
        });
      } catch (err) {
        console.error('Failed to fetch config, assuming human verification is disabled', err);
        setHumanVerificationEnabled(false);
        
        // Log the error
        logDebug('Failed to fetch config', { error: err.message });
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Helper function for remote logging
  const logDebug = (message, data = {}) => {
    try {
      fetch(process.env.REACT_APP_API_URL + '/debug/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message, 
          data,
          timestamp: new Date().toISOString(),
          source: 'AuthContext'
        })
      }).catch(() => {});
    } catch (e) {}
  };

  // Register a new user
  const register = async (username, password) => {
    try {
      setError(null);
      setLoading(true);
      await authApi.register(username, password);
      setLoading(false);
      return true;
    } catch (err) {
      setLoading(false);
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      return false;
    }
  };

  // Login user
  const login = async (username, password, turnstileToken = null) => {
    try {
      setError(null);
      setLoading(true);
      
      // Log attempt with token info
      logDebug('Login attempt', { 
        username,
        humanVerificationEnabled,
        hasToken: !!turnstileToken,
        tokenPrefix: turnstileToken ? turnstileToken.substring(0, 10) : 'none'
      });
      
      // Only include turnstile token if human verification is enabled
      const tokenToSend = humanVerificationEnabled ? turnstileToken : null;
      
      // Always use /auth/login endpoint with JSON
      const data = await authApi.login(username, password, tokenToSend);
      setToken(data.access_token);
      setUser({ authenticated: true });
      setLoading(false);
      
      // Log successful login
      logDebug('Login successful', { username });
      
      return true;
    } catch (err) {
      setLoading(false);
      
      // Log error details
      logDebug('Login error', { 
        error: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const message = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(message);
      return false;
    }
  };

  // Logout user
  const logout = () => {
    removeToken();
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    isAuthenticated: isAuthenticated(),
    humanVerificationEnabled,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;